/**
 * Shared screen for "pick a style template" tools: Change Outfit, Hair Salon, Makeup Styles, Face Expression.
 *
 * Flow: 1 Upload photo → 2 Choose a style → 3 per-tool options → Generate.
 * The style's prompt lives on the server; the app only sends the template id
 * and the tool's options (see ./toolOptions.ts). The face is always kept, so
 * it is never an option. The price is data (GET /credits/pricing).
 */
import { ApiError, api } from '../api/client.js';
import { navigate } from '../router.js';
import { escapeHtml } from '../components/placeholder.js';
import { cardThumbUrl } from '../components/templateCard.js';
import { icon } from '../components/icon.js';
import { toast, toastApiError } from '../ui/toast.js';
import { creditsChanged, getBalance, refreshCredits } from '../state/credits.js';
import { requireAccount, requireSignIn } from '../state/gate.js';
import { withUploadGuide } from '../components/uploadGuide.js';
import { SAMPLE_PHOTOS } from '../content/samplePhotos.js';
import type { ToolOptionsUi } from './toolOptions.js';
import type { CreditPricingConfig, Template, ToolType, UploadFileMeta } from '@loomia/shared';

export interface StyleToolConfig {
  tool: Extract<ToolType, 'change_outfit' | 'hair_salon' | 'makeup_styles' | 'face_expression'>;
  title: string;
  subtitle: string;
  /** Step 2 heading, e.g. "Choose a style". */
  stylesTitle: string;
  seeAllHref: string;
  /** CTA label before the price, e.g. "Generate Outfit". */
  cta: string;
  emptyIcon: string;
  emptyText: string;
  /** Optional consent line shown under the photo step (e.g. for edits of faces). */
  uploadNotice?: string;
  /** Fresh option state for each mount (step 3). */
  makeOptions: () => ToolOptionsUi;
}
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const CLIENT_MAX_BYTES = 25 * 1024 * 1024;

interface Photo {
  status: 'empty' | 'uploading' | 'ready' | 'error';
  progress: number;
  previewUrl: string | null;
  fileId: string | null;
  error: string | null;
}

let renderSeq = 0;
let prevCleanup: (() => void) | null = null;

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

function titleCase(s: string): string {
  return s.replace(/(^|[-_ ])(\w)/g, (_m, sep: string, c: string) => (sep ? ' ' : '') + c.toUpperCase());
}

export function renderStyleTool(root: HTMLElement, query: URLSearchParams, cfg: StyleToolConfig): void {
  const TOOL = cfg.tool;
  const seq = ++renderSeq;
  const alive = (): boolean => seq === renderSeq && root.isConnected;

  const load = async (): Promise<void> => {
    if (prevCleanup) {
      prevCleanup();
      prevCleanup = null;
    }
    root.innerHTML = '<div class="co"><div class="co-skel"></div></div>';
    const slug = query.get('template')?.trim() || null;
    const [pricingR, listR, slugR] = await Promise.allSettled([
      api.getPricing(),
      api.listTemplates({ tool: TOOL, limit: 60 }),
      slug ? api.getTemplate(slug) : Promise.resolve(null),
      refreshCredits().catch(() => null),
    ]);
    if (!alive()) return;
    if (pricingR.status === 'rejected' || listR.status === 'rejected') {
      root.innerHTML = `
        <div class="co"><div class="co-state">${icon('cloud_off')}
          <p>Couldn't load ${escapeHtml(cfg.title)}. Check your connection.</p>
          <button class="btn btn-secondary" data-act="retry">Try again</button></div></div>`;
      root.querySelector('[data-act="retry"]')?.addEventListener('click', () => void load());
      return;
    }
    const templates = listR.value.templates;
    let selected: Template | null = null;
    if (slugR.status === 'fulfilled' && slugR.value && slugR.value.tool === TOOL) {
      selected = slugR.value;
      if (!templates.some((t) => t.id === selected!.id)) templates.unshift(selected);
    }
    mount(pricingR.value, templates, selected ?? templates[0] ?? null);
  };

  const mount = (pricing: CreditPricingConfig, templates: Template[], initial: Template | null): void => {
    let template = initial;
    let tag = 'all';
    const opts = cfg.makeOptions();
    let generating = false;
    let photo: Photo = { status: 'empty', progress: 0, previewUrl: null, fileId: null, error: null };

    const price = (): number => pricing.perTool[TOOL]?.credits ?? template?.creditCost ?? 0;
    const tags = (): string[] => {
      const seen: string[] = [];
      for (const t of templates) for (const x of t.tags ?? []) if (!seen.includes(x)) seen.push(x);
      // Audience tags first (Women / Men), then the rest in data order.
      const lead = ['women', 'men'];
      return [...lead.filter((x) => seen.includes(x)), ...seen.filter((x) => !lead.includes(x))];
    };
    const visible = (): Template[] =>
      tag === 'all' ? templates : templates.filter((t) => (t.tags ?? []).includes(tag));

    const missing = (): string | null => {
      if (!template) return `${cfg.stylesTitle} first`;
      if (photo.status === 'uploading') return 'Uploading…';
      if (photo.status !== 'ready') return 'Add your photo first';
      return null;
    };

    // -- Markup -----------------------------------------------------------

    const step = (n: number, title: string, extra = ''): string =>
      `<div class="co-step"><span class="co-num">${n}</span><h2>${title}</h2>${extra}</div>`;

    const heroHtml = (): string => {
      if (!template) return '';
      const before = photo.previewUrl ?? template.beforeUrl ?? null;
      const after = `<figure class="co-after"><img src="${escapeHtml(template.previewUrl)}" alt="" /><figcaption>Example</figcaption></figure>`;
      if (!before) return `<div class="co-hero is-single">${after}</div>`;
      return `
        <div class="co-hero">
          <figure class="co-before"><img src="${escapeHtml(before)}" alt="" /><figcaption>Before</figcaption></figure>
          <span class="co-arrow" aria-hidden="true">${icon('arrow_forward')}</span>
          ${after}
        </div>`;
    };

    const photoHtml = (): string => {
      const samples =
        SAMPLE_PHOTOS.length === 0
          ? ''
          : `<div class="co-samples"><p class="co-sub">Try these photos</p><div class="co-samples-row">${SAMPLE_PHOTOS.map(
              (s, i) => `<button class="co-sample" data-sample="${i}" aria-label="${escapeHtml(s.label)}"><img src="${escapeHtml(s.url)}" alt="" loading="lazy" /></button>`,
            ).join('')}</div></div>`;
      if (photo.status === 'empty') {
        return `
          <div class="co-photo-row${SAMPLE_PHOTOS.length === 0 ? ' no-samples' : ''}">
            <button class="co-drop" data-act="pick">
              ${icon('add_photo_alternate')}<strong>Add your photo</strong><span>JPG, PNG or WebP · up to 25 MB</span>
            </button>
            ${samples}
          </div>`;
      }
      const label =
        photo.status === 'uploading'
          ? `Uploading… ${Math.round(photo.progress * 100)}%`
          : photo.status === 'error'
            ? escapeHtml(photo.error ?? 'Upload failed')
            : '';
      return `
        <div class="co-photo-row${SAMPLE_PHOTOS.length === 0 ? ' no-samples' : ''}">
          <div class="co-photo is-${photo.status}">
            ${photo.previewUrl ? `<img src="${escapeHtml(photo.previewUrl)}" alt="Your photo" />` : ''}
            ${photo.status === 'uploading' ? `<div class="co-bar"><span style="width:${Math.round(photo.progress * 100)}%"></span></div>` : ''}
            <div class="co-photo-side">
              ${label ? `<span class="co-photo-label" data-photo-label>${label}</span>` : ''}
              <button class="co-link" data-act="pick">${photo.status === 'error' ? 'Try again' : 'Change'}</button>
            </div>
          </div>
          ${samples}
        </div>`;
    };

    const chipsHtml = (): string => {
      const all = ['all', ...tags()];
      if (all.length <= 2) return '';
      return `<div class="co-chips" role="group" aria-label="Style filters">${all
        .map(
          (t) => `<button class="co-chip${t === tag ? ' selected' : ''}" data-tag="${escapeHtml(t)}">${t === 'all' ? 'All' : escapeHtml(titleCase(t))}</button>`,
        )
        .join('')}</div>`;
    };

    const railHtml = (): string => {
      const list = visible();
      if (list.length === 0) return '<p class="co-sub">No styles in this filter yet.</p>';
      return `<div class="co-rail" role="listbox" aria-label="Styles">${list
        .map(
          (t) => `
        <button class="co-style${t.id === template?.id ? ' selected' : ''}" role="option" aria-selected="${t.id === template?.id}" data-tpl="${escapeHtml(t.id)}">
          <img src="${escapeHtml(cardThumbUrl(t.thumbnailUrl))}" alt="" loading="lazy" decoding="async" />
          <span>${escapeHtml(t.title)}</span>
        </button>`,
        )
        .join('')}</div>`;
    };

    const footerHtml = (): string => {
      const c = price();
      const bal = getBalance();
      const short = bal !== null && bal < c ? c - bal : 0;
      const why = missing();
      const btn =
        short > 0 && !why
          ? '<button class="btn btn-primary btn-block" data-act="buy">Get more credits</button>'
          : `<button class="btn btn-primary btn-block" data-act="generate" ${generating ? 'disabled' : ''}>${
              generating ? 'Starting…' : why ? escapeHtml(why) : `${escapeHtml(cfg.cta)} ${icon('toll', { fill: true })} ${c}`
            }</button>`;
      return `
        <div class="co-total${short > 0 ? ' is-short' : ''}">
          <span>Total</span><strong>${icon('toll', { fill: true })} ${c}</strong>
          <small>Failed runs are refunded automatically</small>
        </div>
        ${short > 0 ? `<p class="co-warn">You need ${icon('toll', { fill: true })} ${short} more credits</p>` : ''}
        ${btn}`;
    };

    const paint = (): void => {
      if (!template) {
        root.innerHTML = `<div class="co"><div class="co-state">${icon(cfg.emptyIcon)}<p>${escapeHtml(cfg.emptyText)}</p></div></div>`;
        return;
      }
      root.innerHTML = `
        <div class="co">
          <header class="co-head">
            <button class="co-back" data-act="back" aria-label="Back">${icon('arrow_back')}</button>
            <div class="co-head-text"><h1>${escapeHtml(cfg.title)}</h1><p>${escapeHtml(cfg.subtitle)}</p></div>
            <a class="co-credits" href="#/paywall" aria-label="Get credits">${icon('toll', { fill: true })} ${getBalance() ?? '–'} <span>+</span></a>
          </header>
          ${heroHtml()}
          ${step(1, 'Upload photo')}
          ${photoHtml()}
          ${cfg.uploadNotice ? `<p class="co-note">${icon('info')}${escapeHtml(cfg.uploadNotice)}</p>` : ''}
          ${step(2, escapeHtml(cfg.stylesTitle), `<a class="co-link co-all" href="${escapeHtml(cfg.seeAllHref)}">See all</a>`)}
          ${chipsHtml()}
          ${railHtml()}
          ${opts.html(step)}
          <footer class="co-foot" id="co-foot">${footerHtml()}</footer>
          <input type="file" id="co-file" accept="${ACCEPTED_TYPES.join(',')}" hidden />
        </div>`;
    };

    const paintFooter = (): void => {
      const f = root.querySelector<HTMLElement>('#co-foot');
      if (f) f.innerHTML = footerHtml();
    };

    // -- Upload -----------------------------------------------------------

    const upload = async (file: File): Promise<void> => {
      if (!requireSignIn('Sign in to add your photo.')) return;
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast('Please choose a JPEG, PNG or WebP image.');
        return;
      }
      if (file.size > CLIENT_MAX_BYTES) {
        toast('That image is too large. Please pick one under 25 MB.');
        return;
      }
      if (photo.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(photo.previewUrl);
      photo = { status: 'uploading', progress: 0, previewUrl: URL.createObjectURL(file), fileId: null, error: null };
      const mine = photo;
      paint();
      try {
        const req = await api.requestUpload({
          purpose: 'generation_input',
          contentType: file.type,
          contentLength: file.size,
        });
        await api.putUploadBytes(req.uploadUrl, file, req.uploadHeaders, (r) => {
          if (!alive() || photo !== mine) return;
          mine.progress = r;
          const bar = root.querySelector<HTMLElement>('.co-bar span');
          if (bar) bar.style.width = `${Math.round(r * 100)}%`;
          const label = root.querySelector<HTMLElement>('[data-photo-label]');
          if (label) label.textContent = `Uploading… ${Math.round(r * 100)}%`;
        });
        const meta: UploadFileMeta = await api.completeUpload(req.fileId);
        if (!alive() || photo !== mine) return;
        if (meta.status !== 'ready') throw new Error(meta.error || 'Image processing failed.');
        mine.status = 'ready';
        mine.fileId = meta.id;
        mine.previewUrl = meta.thumbnails?.thumb256 ?? meta.url ?? mine.previewUrl;
      } catch (err) {
        if (!alive() || photo !== mine) return;
        mine.status = 'error';
        mine.error = err instanceof Error ? err.message : 'Upload failed.';
        toastApiError(err, 'Upload failed. Please try again.');
      }
      paint();
    };

    // -- Generate ---------------------------------------------------------

    const generate = async (): Promise<void> => {
      if (generating) return;
      if (!requireAccount('Sign in to generate.')) return;
      const why = missing();
      if (why) {
        toast(why);
        // No photo yet: open the picker right away instead of only saying so.
        if (/^Add /.test(why)) root.querySelector<HTMLElement>('[data-act="pick"]')?.click();
        return;
      }
      const c = price();
      let bal = getBalance();
      if (bal === null) bal = await refreshCredits().catch(() => null);
      if (!alive()) return;
      if (bal !== null && bal < c) {
        navigate('/paywall');
        return;
      }
      generating = true;
      paintFooter();
      try {
        const { job } = await api.submitJob({
          tool: TOOL,
          templateId: template!.id,
          inputFileIds: photo.fileId ? [photo.fileId] : [],
          options: opts.value(),
          idempotencyKey: newId(),
        });
        void refreshCredits().catch(() => undefined);
        navigate(`/result/${job.id}`);
      } catch (err) {
        if (!alive()) return;
        if (err instanceof ApiError && (err.status === 402 || /insufficient|credit/i.test(err.code))) {
          toast('Not enough credits.');
          navigate('/paywall');
        } else {
          toastApiError(err, 'Could not start generation. Please try again.');
        }
      } finally {
        if (alive()) {
          generating = false;
          paintFooter();
        }
      }
    };

    // -- Events (delegated) -----------------------------------------------

    root.onclick = (e) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>(
        '[data-act],[data-tag],[data-tpl],[data-opt],[data-sample]',
      );
      if (!el) return;
      const d = el.dataset;
      if (d.act === 'back') {
        if (history.length > 1) history.back();
        else navigate('/');
      } else if (d.act === 'pick') {
        if (requireSignIn('Sign in to add your photo.')) {
          withUploadGuide(TOOL, () => root.querySelector<HTMLInputElement>('#co-file')?.click());
        }
      } else if (d.act === 'generate') void generate();
      else if (d.act === 'buy') navigate('/paywall');
      else if (d.tag) {
        tag = d.tag;
        paint();
      } else if (d.tpl) {
        template = templates.find((t) => t.id === d.tpl) ?? template;
        paint();
      } else if (d.opt !== undefined) {
        if (opts.onClick(el)) paint();
      } else if (d.sample) {
        const s = SAMPLE_PHOTOS[Number(d.sample)];
        if (s) {
          void fetch(s.url)
            .then((r) => r.blob())
            .then((b) => upload(new File([b], 'sample.webp', { type: b.type || 'image/webp' })))
            .catch(() => toast("Couldn't load that sample photo."));
        }
      }
    };

    root.onchange = (e) => {
      const input = e.target as HTMLInputElement;
      if (input.dataset.opt !== undefined) {
        if (opts.onChange?.(input)) paint();
        return;
      }
      if (input.id !== 'co-file') return;
      const f = input.files?.[0];
      input.value = '';
      if (f) void upload(f);
    };

    const unsub = creditsChanged.on(() => {
      if (alive()) paintFooter();
    });
    prevCleanup = () => {
      unsub();
      if (photo.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(photo.previewUrl);
    };

    paint();
  };

  void load();
}
